import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversationSocket, type SocketFrame } from './useConversationSocket';
import Message from '../documentation/Message';
import BeatLoader from '../ui/BeatLoader';
import useApi from '../api';
import { MessageType } from '../types';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import { useUser } from '../user/UserContext';
import { formatDate } from '../utils/dateFormatter';
import { debounce } from 'lodash';
import useAnalytics from '../analytics/analytics';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Bot, CheckCircle, Sparkles, MousePointerClick, SendHorizontal, Code, Cog, ChevronDown, ChevronUp, ChevronsRight, X, FileText, File as FileIcon, Image as ImageIconLucide, AlertCircle, Loader2, Mail, PhoneCall, ClipboardList, User, UserPlus, Globe, MessageCircle, ChevronLeft, Info, Headset, CircleStop } from "lucide-react";
import RichTextEditor from '@/components/ui/RichTextEditor';
import {
    applyToolLifecycleEvent,
    mergeToolLifecycleMessages,
    normalizeToolLifecycleEvent,
} from './toolLifecycle';
import type { SessionEscalation } from './escalations';

interface ChatProps {
    chatId: string | null;
    /**
     * The session the page already fetched. When its id matches chatId the
     * chat renders from it instead of requesting the same payload again,
     * which takes one round trip off every session switch.
     */
    initialConversation?: any;
    status: string;
    setStatus: (status: string) => void;
    initiatedBy?: string;
    channel?: string;
    emailSubject?: string;
    onDetailsRefreshHint?: (reason?: string) => void;
    orgEvent?: {
        type?: string;
        visitor_id?: string;
        [key: string]: any;
    } | null;
    onBackToSessions?: () => void;
    onShowDetails?: () => void;
}

interface GroupedMessage {
    url: string;
    messages: MessageType[];
}

type OwnershipState = 'ai' | 'owned_by_me' | 'owned_by_other';
type HumanAgent = {
    id?: string;
    first_name?: string;
    last_name?: string;
    name?: string;
    email?: string;
    profile_photo_url?: string | null;
};
type VisitorPresenceState = 'on_site' | 'offline' | 'unknown';
type ActiveVisitorPresence = {
    visitor_id: string;
};

// --- ownership ---------------------------------------------------------------
//
// One snapshot with one writer (setOwnership below). The socket frame
// handlers, the REST loader and the optimistic hand-back all go through it,
// and the rendered 'ai' | 'owned_by_me' | 'owned_by_other' is derived from
// it against the signed-in user rather than stored separately.

type Owner = { id: string; name: string; photoUrl: string | null };
type OwnershipSnapshot = { owner: Owner | null; pending: boolean };
const EMPTY_OWNERSHIP: OwnershipSnapshot = { owner: null, pending: false };

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const ENTER_KEY = '↵';
const MOD_ENTER_KEY = IS_MAC ? '⌘↵' : 'Ctrl+↵';
const Kbd = ({ children }: { children: React.ReactNode }) => (
    <kbd className="ml-1 inline-flex items-center rounded border border-black/15 bg-black/5 px-1 py-0.5 font-mono text-[10px] leading-none opacity-80 dark:border-white/20 dark:bg-white/10">
        {children}
    </kbd>
);

// Steers are instructions for the model, not visitor-facing rich text: flatten
// the editor's HTML to plain text (block ends and <br> become newlines).
const htmlToPlainText = (html: string): string => {
    const withBreaks = (html || '')
        .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n');
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

const getHumanAgentName = (humanAgent?: HumanAgent | null, fallback = 'A teammate') => {
    if (!humanAgent) return fallback;
    return humanAgent.name || [humanAgent.first_name, humanAgent.last_name].filter(Boolean).join(' ') || humanAgent.email || fallback;
};

const getHumanAgentInitials = (humanAgent?: HumanAgent | null, fallbackName?: string) => {
    const firstLast = `${humanAgent?.first_name?.[0] || ''}${humanAgent?.last_name?.[0] || ''}`.trim();
    return (firstLast || humanAgent?.email?.[0] || fallbackName?.[0] || 'A').toUpperCase();
};

const ownerFrom = (
    humanAgent: HumanAgent | null | undefined,
    id?: string | null,
    fallbackName = 'Another agent',
): Owner | null => {
    const ownerId = humanAgent?.id || id;
    if (!ownerId) return null;
    return {
        id: String(ownerId),
        name: getHumanAgentName(humanAgent, fallbackName),
        photoUrl: humanAgent?.profile_photo_url || null,
    };
};

const deriveOwnershipState = (snapshot: OwnershipSnapshot, userId?: string | null): OwnershipState => {
    if (!snapshot.owner) return 'ai';
    return userId && snapshot.owner.id === userId ? 'owned_by_me' : 'owned_by_other';
};

const newClientId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// --- ask_question card activity (visitor answered or skipped the question card) ---

const isQuestionCardActivity = (message: MessageType): boolean => {
    const systemType = (message.message_metadata as any)?.system_type;
    return systemType === 'question_response' || systemType === 'question_skip';
};

// The stored content is an AI briefing ("...do NOT call update_form...") that embeds
// a "Question: Answer; ..." summary — pull the pairs back out for display.
const splitQaPair = (segment: string): { question: string; answer: string } => {
    const q = segment.indexOf('?: ');
    if (q !== -1) return { question: segment.slice(0, q + 1), answer: segment.slice(q + 3) };
    const c = segment.indexOf(': ');
    if (c !== -1) return { question: segment.slice(0, c), answer: segment.slice(c + 2) };
    return { question: '', answer: segment };
};

const getQuestionCardActivity = (message: MessageType): {
    answered: Array<{ question: string; answer: string }>;
    skipped: string[];
} => {
    const meta = (message.message_metadata || {}) as any;
    const content = message.content || '';
    const answered: Array<{ question: string; answer: string }> = [];

    // question_response embeds the summary after "question card: "; question_skip
    // embeds any answered-before-skipping pairs after "update_form for them): ".
    const summaryMatch = content.match(/question card: ([\s\S]*?)\.?\s+The form has been updated/)
        || content.match(/update_form for them\): ([\s\S]*?)\.\s+Do not immediately/);
    if (summaryMatch) {
        answered.push(...summaryMatch[1].split('; ').map(splitQaPair));
    } else if (meta.system_type === 'question_response' && meta.answers && typeof meta.answers === 'object') {
        answered.push(...Object.entries(meta.answers).map(([k, v]) => ({ question: k, answer: String(v) })));
    }

    const skipped: string[] = Array.isArray(meta.skipped_labels)
        ? meta.skipped_labels.filter((label: any) => typeof label === 'string' && label)
        : [];
    return { answered, skipped };
};

// Renders like a visitor message (avatar + left-aligned bubble): answering or
// skipping the question card is the visitor acting, not a system notice.
const QuestionCardActivity = ({ message }: { message: MessageType }) => {
    const skippedCard = (message.message_metadata as any)?.system_type === 'question_skip';
    const { answered, skipped } = getQuestionCardActivity(message);

    return (
        <div className="flex flex-col pb-2 w-full min-w-0">
            <div className="flex gap-2 w-full min-w-0 items-end mr-4">
                <Avatar className="h-7 w-7 flex-shrink-0">
                    <AvatarFallback className="bg-muted text-muted-foreground border border-border">
                        <User className="h-3.5 w-3.5" />
                    </AvatarFallback>
                </Avatar>
                <Card className="relative text-sm min-w-0 max-w-[85%] overflow-hidden shadow-none rounded-2xl rounded-bl-sm bg-muted border-border">
                    <div className="flex items-center gap-1.5 border-b border-border/60 px-3.5 py-1.5">
                        {skippedCard ? (
                            <>
                                <ChevronsRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                <span className="text-xs font-medium text-muted-foreground">
                                    {skipped.length > 1 ? 'Skipped questions' : 'Skipped a question'}
                                </span>
                            </>
                        ) : (
                            <>
                                <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                                    {answered.length > 1 ? 'Answered questions' : 'Answered a question'}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="px-3.5 py-2 space-y-2 min-w-0">
                        {answered.map((pair, i) => (
                            <div key={`answered-${i}`} className="min-w-0">
                                {pair.question && (
                                    <div className="text-xs text-muted-foreground break-words">{pair.question}</div>
                                )}
                                <div className="text-sm text-foreground break-words [overflow-wrap:anywhere]">{pair.answer}</div>
                            </div>
                        ))}
                        {skipped.length > 0 && (
                            <div className="min-w-0 space-y-0.5">
                                {answered.length > 0 && (
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">Skipped</div>
                                )}
                                {skipped.map((label, i) => (
                                    <div key={`skipped-${i}`} className="text-xs text-muted-foreground break-words">{label}</div>
                                ))}
                            </div>
                        )}
                        {answered.length === 0 && skipped.length === 0 && (
                            <div className="text-xs text-muted-foreground">
                                {skippedCard ? 'The visitor dismissed the question card.' : 'The visitor answered the question card.'}
                            </div>
                        )}
                    </div>
                </Card>
            </div>
            {message.created_at && (
                <div className="mt-1 ml-11">
                    <span className="text-[11px] text-muted-foreground/70 leading-none">{formatDate(message.created_at)}</span>
                </div>
            )}
        </div>
    );
};

const Chat: React.FC<ChatProps> = ({
    chatId,
    initialConversation,
    status,
    setStatus,
    initiatedBy,
    channel,
    emailSubject,
    onDetailsRefreshHint,
    orgEvent = null,
    onBackToSessions,
    onShowDetails,
}) => {
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [messages, setMessages] = useState<MessageType[]>([]);
    const [escalations, setEscalations] = useState<SessionEscalation[]>([]);
    const escalationRefreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const [conversationId, setConversationId] = useState('');
    const [conversation, setConversation] = useState<any>(null);

    // Ownership snapshot (see the module-level notes). The ref is written in
    // the same tick as the state so socket handlers never read a stale value.
    const [ownership, setOwnershipSnapshot] = useState<OwnershipSnapshot>(EMPTY_OWNERSHIP);
    const ownershipRef = useRef<OwnershipSnapshot>(EMPTY_OWNERSHIP);
    const setOwnership = useCallback(
        (update: OwnershipSnapshot | ((prev: OwnershipSnapshot) => OwnershipSnapshot)) => {
            const next = typeof update === 'function' ? update(ownershipRef.current) : update;
            ownershipRef.current = next;
            setOwnershipSnapshot(next);
        },
        [],
    );

    // A boolean loses repeated scroll requests when several socket events are
    // batched while it is already true. Keep a monotonic request counter so
    // every live message/send schedules a post-render scroll.
    const [scrollRequest, setScrollRequest] = useState(0);
    // Follow the transcript only while the rep is at (or near) the bottom.
    // Scrolled up to read, live traffic must not yank them down; new
    // messages light up the jump-to-latest pill instead.
    const pinnedToBottomRef = useRef(true);
    const pendingUnseenRef = useRef(false);
    // Identity of the last transcript row the pill logic has seen, so echoes
    // and redeliveries (which patch or drop rows) don't count as new.
    const lastRowKeyRef = useRef<string | null>(null);
    const [scrolledUp, setScrolledUp] = useState(false);
    const [hasUnseenMessages, setHasUnseenMessages] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isVisitorTyping, setIsVisitorTyping] = useState(false);
    const [isAiResponding, setIsAiResponding] = useState(false);
    const [isAiProcessing, setIsAiProcessing] = useState(false);
    // Operator steering: guidance for the AI without taking over
    // (POST /sessions/{id}/steer — backend/api/services/steering_service.py).
    const [steerPending, setSteerPending] = useState(false);
    
    // Pagination state
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    
    // File attachment state
    type AttachmentStatus = 'uploading' | 'uploaded' | 'failed';
    const [pendingAttachments, setPendingAttachments] = useState<Array<{id: string, filename: string, size: number, type: string, url: string, status: AttachmentStatus}>>([]);
    const [isUploading, setIsUploading] = useState(false);
    
    // Visitor presence state
    const [visitorPresenceState, setVisitorPresenceState] = useState<VisitorPresenceState>('unknown');

    const navigate = useNavigate();
    const { get, post, remove, connectWebSocket } = useApi();
    const { user } = useUser();
    const { captureEvent } = useAnalytics();

    const chatRef = useRef<HTMLDivElement>(null);
    const topSentinelRef = useRef<HTMLDivElement>(null);
    const loadOlderRef = useRef<() => void>(() => {});
    const statusRef = useRef<string>(status);
    const userRef = useRef(user);
    // Which chat id the page currently wants. Written synchronously at the
    // top of the load effect, so any async continuation can compare against
    // it without waiting for a passive effect to catch up.
    const activeChatIdRef = useRef<string | null>(chatId);
    // Bumped on every reset load; a continuation that finds it changed bails.
    const loadGenerationRef = useRef(0);
    // The chat id whose transcript has finished loading (drives auto-takeover).
    const loadedChatIdRef = useRef<string | null>(null);
    const conversationVisitorIdRef = useRef<string | null>(null);
    const initialConversationRef = useRef(initialConversation);
    const autoTakeoverAttemptedForRef = useRef<string | null>(null);

    // Tab visibility and notification state
    const titleFlashIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const originalTitleRef = useRef<string>(document.title);
    const notificationAudioContextRef = useRef<AudioContext | null>(null);
    const visitorPresenceRef = useRef<VisitorPresenceState>(visitorPresenceState);
    const onDetailsRefreshHintRef = useRef(onDetailsRefreshHint);

    userRef.current = user;
    initialConversationRef.current = initialConversation;

    // Keep status ref in sync for auto_takeover retry logic
    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    useEffect(() => {
        visitorPresenceRef.current = visitorPresenceState;
    }, [visitorPresenceState]);

    useEffect(() => {
        onDetailsRefreshHintRef.current = onDetailsRefreshHint;
    }, [onDetailsRefreshHint]);

    const getRef = useRef(get);
    getRef.current = get;

    const ownershipState = deriveOwnershipState(ownership, user?.id);
    const takeoverPending = ownership.pending;
    const ownerAgentName = ownership.owner?.name ?? '';
    const ownerAgentPhotoUrl = ownership.owner?.photoUrl ?? null;
    const currentOwnershipState = () => deriveOwnershipState(ownershipRef.current, userRef.current?.id);

    const setPresenceStateIfChanged = useCallback((nextState: VisitorPresenceState) => {
        if (visitorPresenceRef.current !== nextState) {
            visitorPresenceRef.current = nextState;
            setVisitorPresenceState(nextState);
        }
    }, []);

    const refreshVisitorPresence = useCallback(async (visitorId: string, setUnknownOnError: boolean = false) => {
        try {
            const activeVisitorsResponse = await getRef.current('/active-visitors');
            // The rep may have moved to another visitor's session while this
            // request was in flight; its answer is about someone else now.
            if (conversationVisitorIdRef.current !== visitorId) return;
            if (activeVisitorsResponse.data?.active_visitors) {
                const activeVisitors = activeVisitorsResponse.data.active_visitors as ActiveVisitorPresence[];
                const onSite = activeVisitors.some((v) => v.visitor_id === visitorId);
                setPresenceStateIfChanged(onSite ? 'on_site' : 'offline');
            } else if (setUnknownOnError) {
                setPresenceStateIfChanged('unknown');
            }
        } catch (error) {
            console.error('Error checking visitor presence:', error);
            if (setUnknownOnError) {
                setPresenceStateIfChanged('unknown');
            }
        }
    }, [setPresenceStateIfChanged]);

    useEffect(() => {
        if (!orgEvent?.type || !conversation?.visitor_id) return;
        if (orgEvent.visitor_id !== conversation.visitor_id) return;

        if (
            orgEvent.type === 'visitor_active' ||
            orgEvent.type === 'visitor_delta_update' ||
            orgEvent.type === 'visitor_page_change'
        ) {
            setPresenceStateIfChanged('on_site');
        } else if (orgEvent.type === 'visitor_inactive') {
            setPresenceStateIfChanged('offline');
        }
    }, [orgEvent, conversation?.visitor_id, setPresenceStateIfChanged]);

    // Periodic fallback polling to recover from missed org-socket presence events.
    useEffect(() => {
        if (!conversation?.visitor_id) {
            setPresenceStateIfChanged('unknown');
            return;
        }

        const visitorId = conversation.visitor_id;
        void refreshVisitorPresence(visitorId, false);

        const intervalId = setInterval(() => {
            void refreshVisitorPresence(visitorId, false);
        }, 30000);

        return () => {
            clearInterval(intervalId);
        };
    }, [conversation?.visitor_id, refreshVisitorPresence, setPresenceStateIfChanged]);

    // Keep one audio context for the chat instead of creating a new context
    // for every visitor reply. A takeover/send gesture primes it so browsers
    // that require user interaction can play later WebSocket notifications.
    useEffect(() => {
        return () => {
            const audioContext = notificationAudioContextRef.current;
            notificationAudioContextRef.current = null;
            if (audioContext && audioContext.state !== 'closed') {
                void audioContext.close();
            }
        };
    }, []);

    // Track tab visibility and refresh stale conversations
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // Stop flashing title when tab becomes visible
                stopTitleFlash();
                // Re-fetch to pick up anything that arrived while hidden. Same
                // loader as the initial load, so it is generation-guarded.
                const currentChatId = activeChatIdRef.current;
                if (currentChatId) {
                    void loadConversationRef.current(currentChatId, { reset: false });
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopTitleFlash();
        };
    }, []);

    const startTitleFlash = () => {
        // Don't start if already flashing
        if (titleFlashIntervalRef.current) return;
        
        let isOriginal = true;
        titleFlashIntervalRef.current = setInterval(() => {
            document.title = isOriginal ? '🔔 New Message!' : originalTitleRef.current;
            isOriginal = !isOriginal;
        }, 1000); // Flash every second
    };

    const stopTitleFlash = () => {
        if (titleFlashIntervalRef.current) {
            clearInterval(titleFlashIntervalRef.current);
            titleFlashIntervalRef.current = null;
            document.title = originalTitleRef.current;
        }
    };

    const getNotificationAudioContext = () => {
        const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextConstructor) return null;

        if (!notificationAudioContextRef.current || notificationAudioContextRef.current.state === 'closed') {
            notificationAudioContextRef.current = new AudioContextConstructor();
        }
        return notificationAudioContextRef.current;
    };

    const prepareNotificationSound = () => {
        try {
            const audioContext = getNotificationAudioContext();
            if (audioContext?.state === 'suspended') {
                void audioContext.resume().catch(() => undefined);
            }
        } catch (err) {
            console.warn('Could not initialize notification sound:', err);
        }
    };

    const playNotificationSound = () => {
        try {
            const audioContext = getNotificationAudioContext();
            if (!audioContext) return;
            
            // Create an ascending chime with harmonic richness
            const playChime = (frequency: number, startTime: number, duration: number, gain: number) => {
                // Main oscillator
                const osc = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                const filter = audioContext.createBiquadFilter();
                
                // Use a triangle wave for warmth
                osc.type = 'triangle';
                osc.frequency.value = frequency;
                
                // Add a subtle harmonic with a second oscillator
                const osc2 = audioContext.createOscillator();
                const gain2 = audioContext.createGain();
                osc2.type = 'sine';
                osc2.frequency.value = frequency * 2; // One octave higher
                
                // Low-pass filter for smoothness
                filter.type = 'lowpass';
                filter.frequency.value = 2000;
                filter.Q.value = 1;
                
                // Connect the audio graph
                osc.connect(filter);
                osc2.connect(gain2);
                filter.connect(gainNode);
                gain2.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                // Envelope with quick attack and smooth decay
                gainNode.gain.setValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.005);
                gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
                
                gain2.gain.setValueAtTime(0, startTime);
                gain2.gain.linearRampToValueAtTime(gain * 0.3, startTime + 0.005);
                gain2.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
                
                osc.start(startTime);
                osc.stop(startTime + duration);
                osc2.start(startTime);
                osc2.stop(startTime + duration);
            };
            
            const scheduleChime = () => {
                const now = audioContext.currentTime;
                // Create a pleasant ascending melody: C5 -> E5 -> G5 (C major chord)
                playChime(523.25, now, 0.25, 0.25);        // C5
                playChime(659.25, now + 0.08, 0.25, 0.2);  // E5
                playChime(783.99, now + 0.16, 0.35, 0.25); // G5 (held longer)
            };

            if (audioContext.state === 'suspended') {
                void audioContext.resume().then(scheduleChime).catch((err) => {
                    console.warn('Could not resume notification sound:', err);
                });
            } else {
                scheduleChime();
            }
            
        } catch (err) {
            console.warn('Could not play notification sound:', err);
        }
    };

    const scrollToBottom = useCallback(() => {
        if (chatRef.current) {
            const scrollContainer = chatRef.current.querySelector('[data-radix-scroll-area-viewport]');
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }
    }, []);

    // 'message': a new transcript row (flags the pill when scrolled up).
    // 'activity': typing/responding indicators (follow only if pinned).
    // 'force': the rep's own send, always jump to the bottom.
    const requestScrollToBottom = useCallback((kind: 'message' | 'activity' | 'force' = 'message') => {
        if (kind === 'force') pinnedToBottomRef.current = true;
        if (kind === 'message') pendingUnseenRef.current = true;
        setScrollRequest((request) => request + 1);
    }, []);

    const jumpToLatest = useCallback(() => {
        pinnedToBottomRef.current = true;
        setHasUnseenMessages(false);
        setScrolledUp(false);
        scrollToBottom();
    }, [scrollToBottom]);

    const sendTypingStatus = (typing: boolean) => {
        if (currentOwnershipState() === 'owned_by_me' && activeChatIdRef.current) {
            socket.send(activeChatIdRef.current, { type: 'agent_typing', is_typing: typing });
        }
    };

    const debouncedSendTypingStatus = useRef(
        debounce((typing: boolean) => sendTypingStatus(typing), 300)
    ).current;

    const handleInputChange = (newValue: string) => {
        setUserInput(newValue);

        if (newValue && !isTyping) {
            setIsTyping(true);
            sendTypingStatus(true);
        } else if (!newValue && isTyping) {
            setIsTyping(false);
            sendTypingStatus(false);
        } else {
            debouncedSendTypingStatus(!!newValue);
        }
    };

    useEffect(() => {
        return () => {
            if (isTyping) {
                sendTypingStatus(false);
            }
            debouncedSendTypingStatus.cancel();
        };
    }, [isTyping, debouncedSendTypingStatus]);

    useEffect(() => {
        // Wait one frame for Radix ScrollArea and message content to finish
        // laying out before reading scrollHeight.
        const last: any = messages[messages.length - 1];
        const lastKey = last ? String(last.client_id ?? last.id ?? '') : null;
        const unseen = pendingUnseenRef.current && lastKey !== lastRowKeyRef.current;
        pendingUnseenRef.current = false;
        lastRowKeyRef.current = lastKey;
        if (!pinnedToBottomRef.current) {
            if (unseen) setHasUnseenMessages(true);
            return;
        }
        const frameId = window.requestAnimationFrame(scrollToBottom);
        return () => window.cancelAnimationFrame(frameId);
    }, [scrollRequest, scrollToBottom]);

    const handleTakeOverChat = () => {
        // No status gate: the backend claim only requires that nobody else
        // owns the conversation, and taking over an inactive session is the
        // supported way to reach an on-site visitor who closed the chat (the
        // first rep message pushes open_conversation to their widget).
        const targetId = activeChatIdRef.current;
        if (!targetId || ownershipRef.current.pending) return;
        prepareNotificationSound();
        // Only mark pending once the frame is actually on the wire; a send
        // refused (socket still connecting, or serving another chat) must
        // not leave the button stuck on "Taking over…".
        if (!socket.send(targetId, { type: 'take_over_chat' })) {
            toast.error('Still connecting to this conversation, please try again in a moment.', toastConfig);
            return;
        }
        setOwnership((prev) => ({ ...prev, pending: true }));
    };

    const handleHandBackChat = () => {
        const targetId = activeChatIdRef.current;
        if (!targetId) return;
        if (!socket.send(targetId, { type: 'hand_back_chat' })) {
            toast.error('Still connecting to this conversation, please try again in a moment.', toastConfig);
            return;
        }
        // Optimistic: the server confirms with hand_back_chat, or reverts us
        // with hand_back_rejected.
        setOwnership(EMPTY_OWNERSHIP);
    };

    // Steer the AI from the shared composer: the text is guidance for the
    // model (POST /sessions/{id}/steer, mode 'now'), never shown to the
    // visitor. It queues behind a reply in flight and wakes an idle or
    // inactive conversation otherwise.
    const handleSteer = async () => {
        const targetId = activeChatIdRef.current;
        const content = htmlToPlainText(userInput);
        if (!targetId || !content || steerPending || ownershipRef.current.pending) return;
        if (pendingAttachments.length > 0) {
            toast.warning('Attachments go to the visitor. Use Send to take over and deliver them, or remove them to steer the AI.', toastConfig);
            return;
        }
        setSteerPending(true);
        try {
            const response = await post(`/sessions/${targetId}/steer`, { content, mode: 'now' });
            const result = response?.data || {};
            if (activeChatIdRef.current !== targetId) return;
            if (result.message) {
                // The loop can persist and echo the row (real id, client_id)
                // before this response lands. If it has, keep the echo and only
                // fill in the operator profile; otherwise show the optimistic
                // row, which the echo later replaces by client_id.
                const incoming = { ...result.message } as MessageType & { client_id?: string };
                setMessages((prev) => {
                    const idx = incoming.client_id
                        ? prev.findIndex((m: any) => m.client_id && m.client_id === incoming.client_id)
                        : -1;
                    if (idx === -1) return [...prev, incoming];
                    const next = prev.slice();
                    next[idx] = { ...next[idx], human_agent: (next[idx] as any).human_agent || incoming.human_agent };
                    return next;
                });
                requestScrollToBottom('force');
            }
            setUserInput('');
            setIsAiProcessing(true);
            const offline = visitorPresenceRef.current === 'offline';
            toast.success(
                result.ai_responding
                    ? 'Queued. The AI acts on it right after its current reply.'
                    : offline
                        ? 'The AI is acting on it now. The visitor sees the reply when they return.'
                        : 'The AI is acting on it now.',
                toastConfig,
            );
        } catch (error: any) {
            toast.error(error?.detail || 'Could not steer the AI. Please try again.', toastConfig);
        } finally {
            setSteerPending(false);
        }
    };

    // The composer's outgoing rep message, or null (with a toast) when
    // attachments are not ready. Shared by Send and Take over & send.
    const buildOutgoingMessage = (): any | null => {
        const uploadedAttachments = pendingAttachments.filter(att => att.status === 'uploaded');
        if (pendingAttachments.some(att => att.status === 'uploading')) {
            toast.warning('Please wait for files to finish uploading', toastConfig);
            return null;
        }
        if (pendingAttachments.some(att => att.status === 'failed')) {
            toast.warning('Please remove failed attachments before sending', toastConfig);
            return null;
        }
        if (!userInput.trim() && uploadedAttachments.length === 0) return null;
        const message: any = {
            type: 'message',
            content: userInput,
            role: 'assistant',
            created_at: new Date().toISOString(),
            human_agent: user,
            client_id: newClientId()
        };
        if (uploadedAttachments.length > 0) {
            const attachmentsToSend = uploadedAttachments.map(({ status, ...att }) => att);
            message.attachments = attachmentsToSend;
            message.message_metadata = { attachments: attachmentsToSend };
            captureEvent('rep_upload_attachment', {
                conversation_id: conversationId,
                attachment_count: attachmentsToSend.length,
                attachment_types: attachmentsToSend.map(att => att.type),
                attachment_sizes: attachmentsToSend.map(att => att.size),
                total_size: attachmentsToSend.reduce((sum, att) => sum + att.size, 0)
            });
        }
        return message;
    };

    // Take over and send in one go: the claim frame and the message frame go
    // back to back on the socket and the server's per-conversation FIFO
    // processes them in order (the Slack relay takes over the same way). If
    // the claim loses to another rep, the message is refused with
    // rep_message_rejected, which pulls the optimistic row and gives the
    // text back.
    const handleTakeOverAndSend = () => {
        const targetId = activeChatIdRef.current;
        if (!targetId || ownershipRef.current.pending) return;
        const message = buildOutgoingMessage();
        if (!message) return;
        prepareNotificationSound();
        setIsTyping(false);
        if (!socket.send(targetId, { type: 'take_over_chat' })) {
            toast.error('Still connecting to this conversation, please try again in a moment.', toastConfig);
            return;
        }
        setOwnership((prev) => ({ ...prev, pending: true }));
        if (!socket.send(targetId, message)) {
            // The claim is on the wire; the text stays in the composer so it
            // can be sent once we own the conversation.
            toast.error('Reconnecting to this conversation, your message was kept.', toastConfig);
            return;
        }
        appendMessage(message, { force: true });
        setUserInput('');
        setPendingAttachments([]);
    };

    useEffect(() => {
        pinnedToBottomRef.current = true;
        setScrolledUp(false);
        setHasUnseenMessages(false);
        const frameId = window.requestAnimationFrame(scrollToBottom);
        return () => window.cancelAnimationFrame(frameId);
    }, [conversationId, scrollToBottom]);

    // Track whether the rep is reading history. Programmatic scrolls to the
    // bottom fire this too, which is what re-pins after a jump.
    useEffect(() => {
        const viewport = chatRef.current?.querySelector('[data-radix-scroll-area-viewport]');
        if (!viewport) return;
        const onScroll = () => {
            const distance = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
            const pinned = distance < 80;
            pinnedToBottomRef.current = pinned;
            setScrolledUp(!pinned);
            if (pinned) setHasUnseenMessages(false);
        };
        viewport.addEventListener('scroll', onScroll, { passive: true });
        return () => viewport.removeEventListener('scroll', onScroll);
    }, [conversationId, initialLoadComplete, loading]);

    // Infinite scroll: observe a sentinel at the top of the message list and
    // load older messages as it scrolls into view. More reliable than a
    // scrollTop threshold, and the ref guarantees we always call the latest
    // loadOlderMessages (with current state) rather than a stale closure.
    useEffect(() => {
        if (!initialLoadComplete) return;
        const root = chatRef.current?.querySelector('[data-radix-scroll-area-viewport]');
        const sentinel = topSentinelRef.current;
        if (!root || !sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadOlderRef.current();
                }
            },
            { root, rootMargin: '150px 0px 0px 0px', threshold: 0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [initialLoadComplete, hasMoreMessages]);

    const handleFileUpload = async (file: File): Promise<{id: string, filename: string, size: number, type: string, url: string}> => {
        // Generate temporary ID for optimistic UI
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const tempAttachment = {
            id: tempId,
            filename: file.name,
            size: file.size,
            type: file.type,
            url: '',
            status: 'uploading' as AttachmentStatus
        };
        
        // Optimistically add to UI with uploading state
        setPendingAttachments(prev => [...prev, tempAttachment]);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            // Don't manually set headers - useApi's post method handles FormData automatically
            const response = await post(`/sessions/${conversationId}/upload-attachment`, formData);
            
            if (response && response.data) {
                // Update with real data and mark as uploaded
                setPendingAttachments(prev => 
                    prev.map(att => att.id === tempId 
                        ? { ...response.data, status: 'uploaded' as AttachmentStatus }
                        : att
                    )
                );
                return response.data;
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Failed to upload file. Click X to remove.', toastConfig);
            
            // Mark as failed instead of removing
            setPendingAttachments(prev => 
                prev.map(att => att.id === tempId 
                    ? { ...att, status: 'failed' as AttachmentStatus }
                    : att
                )
            );
            throw error;
        }
    };

    const removeAttachment = async (attachmentId: string) => {
        try {
            // Only try to delete from S3 if it's a real ID (not a temp ID from failed upload)
            if (!attachmentId.startsWith('temp-')) {
                await remove(`/sessions/${conversationId}/attachment/${attachmentId}`);
            }
            
            // Remove from pending attachments
            setPendingAttachments(prev => prev.filter(att => att.id !== attachmentId));
        } catch (error) {
            console.error('Error removing attachment:', error);
            toast.error('Failed to remove attachment', toastConfig);
        }
    };

    const sendMessage = () => {
        // Only the confirmed owner sends as a rep (not mid-takeover).
        if (ownershipState !== 'owned_by_me' || takeoverPending) return;
        const message = buildOutgoingMessage();
        if (!message) return;
        prepareNotificationSound();
        setIsTyping(false);
        // The hook refuses unless the socket is open and serves exactly
        // this conversation, so the text can never reach another visitor.
        if (!socket.send(conversationId, message)) {
            toast.error('Reconnecting to this conversation, please try again in a moment.', toastConfig);
            return;
        }
        appendMessage(message, { force: true });
        setUserInput('');
        setPendingAttachments([]); // Clear attachments after sending
    };

    // Function to load older messages
    const loadOlderMessages = async () => {
        if (!conversationId || isLoadingOlder || !hasMoreMessages || messages.length === 0) return;
        
        try {
            setIsLoadingOlder(true);
            
            // Find the oldest message timestamp
            const oldestMessage = messages.reduce((oldest, msg) => {
                const msgDate = new Date(msg.created_at.endsWith('Z') ? msg.created_at : `${msg.created_at}Z`);
                const oldestDate = new Date(oldest.created_at.endsWith('Z') ? oldest.created_at : `${oldest.created_at}Z`);
                return msgDate < oldestDate ? msg : oldest;
            }, messages[0]);
            
            const beforeTimestamp = oldestMessage.created_at.endsWith('Z') 
                ? oldestMessage.created_at 
                : `${oldestMessage.created_at}Z`;
            
            // Get scroll container and save current scroll position
            const scrollContainer = chatRef.current?.querySelector('[data-radix-scroll-area-viewport]');
            const previousScrollHeight = scrollContainer?.scrollHeight || 0;
            
            const response = await get(`/sessions/${conversationId}?limit=50&before_timestamp=${beforeTimestamp}`);
            const olderMessages = response.data.messages;
            
            // Don't update if no new messages were returned
            if (olderMessages.length === 0) {
                setHasMoreMessages(false);
                return;
            }
            
            setMessages((prevMessages) =>
                mergeToolLifecycleMessages([...olderMessages, ...prevMessages])
            );
            setHasMoreMessages(response.data.has_more_messages);
            
            // Restore scroll position after messages are loaded
            // Use requestAnimationFrame to ensure DOM has updated
            requestAnimationFrame(() => {
                if (scrollContainer) {
                    const newScrollHeight = scrollContainer.scrollHeight;
                    const restoredPosition = newScrollHeight - previousScrollHeight;
                    // Ensure we're not at position 0 to prevent immediate re-trigger
                    scrollContainer.scrollTop = Math.max(restoredPosition, 1);
                }
            });
        } catch (error) {
            console.error('Error loading older messages:', error);
        } finally {
            setIsLoadingOlder(false);
        }
    };

    // Keep the ref pointed at the latest loadOlderMessages so the
    // IntersectionObserver always calls a fresh closure (current messages/state).
    loadOlderRef.current = loadOlderMessages;

    // --- transcript ----------------------------------------------------------
    // Dedup by identity, never by ownership. The server echoes every persisted
    // frame with its id, and rep messages carry the client_id we tagged the
    // optimistic row with, so the echo replaces the optimistic row and any
    // redelivery is dropped. (The old "drop assistant frames while I own the
    // chat" rule also dropped teammates' Slack replies and system rows.)
    const appendMessage = useCallback((incoming: MessageType & { client_id?: string }, { force = false }: { force?: boolean } = {}) => {
        setMessages((prev) => {
            if (incoming.client_id) {
                const idx = prev.findIndex((m: any) => m.client_id && m.client_id === incoming.client_id);
                if (idx !== -1) {
                    const next = prev.slice();
                    next[idx] = { ...prev[idx], ...incoming };
                    return next;
                }
            }
            if (incoming.id && prev.some((m) => m.id === incoming.id)) return prev;
            return mergeToolLifecycleMessages([...prev, incoming]);
        });
        requestScrollToBottom(force ? 'force' : 'message');
    }, [requestScrollToBottom]);

    // --- loading -------------------------------------------------------------
    const applyConversation = useCallback((data: any, { keepLive }: { keepLive: boolean }) => {
        const loaded = mergeToolLifecycleMessages(data.messages || []);
        const lastLoaded: any = loaded[loaded.length - 1];
        lastRowKeyRef.current = lastLoaded ? String(lastLoaded.client_id ?? lastLoaded.id ?? '') : null;
        if (keepLive) {
            // The socket opens in parallel with this fetch; frames that landed
            // meanwhile and are newer than the payload stay at the end.
            setMessages((prev) => {
                const known = new Set(loaded.map((m) => m.id).filter(Boolean));
                const live = prev.filter((m: any) => (m.id && !known.has(m.id)) || (!m.id && m.client_id));
                return live.length ? mergeToolLifecycleMessages([...loaded, ...live]) : loaded;
            });
        } else {
            setMessages(loaded);
        }
        setConversationId(data.id);
        setConversation(data);
        setEscalations(Array.isArray(data.escalations) ? data.escalations : []);
        conversationVisitorIdRef.current = data.visitor_id || null;
        setStatus(data.status);
        setHasMoreMessages(!!data.has_more_messages);
        setInitialLoadComplete(true);
        setOwnership({ owner: ownerFrom(data.human_agent, data.human_agent_id), pending: false });
    }, [setOwnership, setStatus]);

    /**
     * Load a conversation. `reset` clears every per-conversation piece of
     * state first (a session switch); without it the transcript and
     * ownership are refreshed in place (tab focus, reconnect, rejection).
     * Every await is guarded: a continuation whose chat id is no longer the
     * one on screen does nothing.
     */
    const loadConversation = useCallback(async (
        targetChatId: string,
        opts: { reset: boolean; preloaded?: any },
    ): Promise<boolean> => {
        if (opts.reset) loadGenerationRef.current += 1;
        const generation = loadGenerationRef.current;
        const isStale = () => generation !== loadGenerationRef.current || activeChatIdRef.current !== targetChatId;

        if (opts.reset) {
            setLoading(true);
            setLoadError(false);
            setOwnership(EMPTY_OWNERSHIP);
            setMessages([]);
            setConversation(null);
            setConversationId('');
            conversationVisitorIdRef.current = null;
            loadedChatIdRef.current = null;
            setPendingAttachments([]);
            setUserInput('');
            setIsTyping(false);
            setIsVisitorTyping(false);
            setIsAiResponding(false);
            setIsAiProcessing(false);
            setHasMoreMessages(false);
            setInitialLoadComplete(false);
            setPresenceStateIfChanged('unknown');
        }
        try {
            let data = opts.preloaded;
            if (!data) {
                const response = await getRef.current(`/sessions/${targetChatId}?limit=50`);
                if (isStale()) return false;
                data = response.data;
            }
            if (isStale()) return false;
            applyConversation(data, { keepLive: opts.reset });
            loadedChatIdRef.current = targetChatId;
            return true;
        } catch (error) {
            console.error('Error fetching conversation:', error);
            if (!isStale() && opts.reset) setLoadError(true);
            return false;
        } finally {
            if (!isStale() && opts.reset) setLoading(false);
        }
    }, [applyConversation, setOwnership, setPresenceStateIfChanged]);
    const loadConversationRef = useRef(loadConversation);
    loadConversationRef.current = loadConversation;

    useEffect(() => {
        activeChatIdRef.current = chatId;
        if (!chatId) return;
        // The page fetched this session to render its header and details;
        // reuse that payload instead of asking for it a second time.
        const preloaded = initialConversationRef.current?.id === chatId ? initialConversationRef.current : undefined;
        void loadConversation(chatId, { reset: true, preloaded });
        return () => {
            loadGenerationRef.current += 1;
            stopTitleFlash();
        };
    }, [chatId, loadConversation]);

    // The escalate tool's result never streams (the widget shares this
    // socket), so the rep comes from the escalation event. It is written a
    // moment after the call; read it back twice to cover a slow routing pass.
    const refreshEscalations = useCallback(async (targetChatId: string) => {
        try {
            const response = await getRef.current(`/sessions/${targetChatId}/escalations`);
            if (activeChatIdRef.current !== targetChatId) return;
            setEscalations(Array.isArray(response.data?.escalations) ? response.data.escalations : []);
            onDetailsRefreshHintRef.current?.('escalation');
        } catch (error) {
            console.error('Error fetching escalations:', error);
        }
    }, []);

    const scheduleEscalationsRefresh = useCallback(() => {
        const targetChatId = activeChatIdRef.current;
        if (!targetChatId || !userRef.current) return;
        escalationRefreshTimersRef.current.forEach(clearTimeout);
        escalationRefreshTimersRef.current = [3000, 10000].map((delay) =>
            setTimeout(() => void refreshEscalations(targetChatId), delay)
        );
    }, [refreshEscalations]);

    useEffect(() => () => {
        escalationRefreshTimersRef.current.forEach(clearTimeout);
    }, [chatId]);

    // --- socket ----------------------------------------------------------------
    function handleFrame(data: SocketFrame) {
        const lifecycleEvent = normalizeToolLifecycleEvent(data);
        if (lifecycleEvent) {
            setMessages((prev) => applyToolLifecycleEvent(prev, lifecycleEvent));
            requestScrollToBottom();
            if (data.type === 'tool_output') {
                onDetailsRefreshHintRef.current?.('tool_output');
                return;
            }
            if (data.type === 'tool_call') {
                setIsAiProcessing(true);
                if (lifecycleEvent.tool_name === 'escalate_to_human') {
                    scheduleEscalationsRefresh();
                }
                return;
            }
        }

        switch (data.type) {
            case 'message': {
                const systemType = data.message_metadata?.system_type;
                if (
                    systemType === 'form_submission'
                    || systemType === 'action_result'
                    || systemType === 'assessment_start'
                    || systemType === 'calendly_booking'
                    || systemType === 'lead_landing'
                ) {
                    onDetailsRefreshHintRef.current?.(`message:${systemType}`);
                }
                appendMessage({
                    id: data.id,
                    role: data.role,
                    content: data.content,
                    created_at: data.created_at || new Date().toISOString(),
                    message_metadata: data.message_metadata,
                    human_agent: data.human_agent,
                    agent_name: data.agent_name,
                    client_id: data.client_id,
                } as MessageType & { client_id?: string });
                if (data.role === 'user') {
                    setIsVisitorTyping(false);
                    // An owned live chat chimes even while visible; a rep
                    // reading elsewhere on the page has no other cue. Title
                    // flashing stays specific to background tabs.
                    if (currentOwnershipState() === 'owned_by_me') playNotificationSound();
                    if (document.hidden) startTitleFlash();
                } else if (data.role === 'assistant' && !data.human_agent) {
                    setIsAiProcessing(false);
                }
                return;
            }
            case 'assistant': {
                // Only the closing chunk of a streamed reply is rendered here.
                // Some closers (credits exhausted, conversation missing) send
                // final_message: true with the text in `content`.
                if (!data.final_message) return;
                const content = typeof data.final_message === 'string'
                    ? data.final_message
                    : (typeof data.content === 'string' ? data.content : '');
                if (!content) return;
                const liveMetadata: Record<string, any> = {};
                if (data.stopped) liveMetadata.stopped = true;
                if (Array.isArray(data.citations)) liveMetadata.citations = { shown: data.citations };
                appendMessage({
                    id: data.message_id || undefined,
                    role: 'assistant',
                    content,
                    created_at: new Date().toISOString(),
                    message_metadata: Object.keys(liveMetadata).length > 0 ? liveMetadata : undefined,
                });
                // A mixed text+tool hop closes its message but the turn
                // continues with an inline continuation.
                if (!data.continuation_pending) setIsAiProcessing(false);
                return;
            }
            case 'take_over_chat': {
                const owner = ownerFrom(data.human_agent, data.agent_id, data.agent_name || 'Another agent');
                setOwnership({ owner, pending: false });
                if (owner && owner.id === userRef.current?.id) {
                    captureEvent('rep_takeover_chat', {
                        conversation_id: activeChatIdRef.current,
                        agent_id: owner.id,
                        agent_name: data.agent_name,
                    });
                }
                appendMessage(data as MessageType);
                return;
            }
            case 'take_over_rejected': {
                if (data.rejected_agent_id !== userRef.current?.id) return;
                toast.error(data.message, toastConfig);
                setOwnership({
                    owner: ownerFrom(data.current_owner, data.current_owner_id, data.current_owner_name || 'Another agent'),
                    pending: false,
                });
                return;
            }
            case 'hand_back_chat': {
                // Ungated on purpose: idle reaper, Slack, another rep.
                setOwnership(EMPTY_OWNERSHIP);
                setIsAiProcessing(false);
                appendMessage(data as MessageType);
                return;
            }
            case 'hand_back_rejected': {
                if (data.rejected_agent_id !== userRef.current?.id) return;
                toast.error(data.message || 'Only the rep who owns this conversation can hand it back.', toastConfig);
                setOwnership({ owner: ownerFrom(null, data.current_owner_id), pending: false });
                if (activeChatIdRef.current) void loadConversationRef.current(activeChatIdRef.current, { reset: false });
                return;
            }
            case 'rep_message_rejected': {
                // The server refused our message because we do not own this
                // conversation (idle hand-back, another rep, a drifted
                // socket). Pull the optimistic row, give the text back, and
                // reload the real ownership.
                if (data.rejected_agent_id !== userRef.current?.id) return;
                toast.error(data.message || 'Your message was not sent.', toastConfig);
                if (data.client_id) {
                    setMessages((prev) => prev.filter((m: any) => m.client_id !== data.client_id));
                }
                if (typeof data.content === 'string' && data.content) {
                    setUserInput((prev) => prev || data.content);
                }
                setOwnership((prev) => ({
                    owner: data.reason === 'owned_by_other_rep' ? prev.owner : null,
                    pending: false,
                }));
                if (activeChatIdRef.current) void loadConversationRef.current(activeChatIdRef.current, { reset: false });
                return;
            }
            case 'status': {
                // Announce only real transitions: the server publishes
                // `inactive` on every visitor socket close and the widget
                // sends `active` on every connect, so a multi-page visit is
                // a stream of both.
                if (data.status !== statusRef.current) {
                    if (data.status === 'active') toast.info('Visitor is active', toastConfig);
                    if (data.status === 'inactive') toast.info('Visitor closed the chat', toastConfig);
                }
                setStatus(data.status);
                return;
            }
            case 'lead_created':
            case 'lead_updated':
                onDetailsRefreshHintRef.current?.(data.type);
                return;
            case 'operator_action_result':
                onDetailsRefreshHintRef.current?.('operator_action_result');
                return;
            case 'visitor_typing':
                setIsVisitorTyping(!!data.is_typing);
                requestScrollToBottom('activity');
                return;
            case 'ai_responding':
                setIsAiResponding(!!data.responding);
                if (!data.responding) setIsAiProcessing(false);
                requestScrollToBottom('activity');
                return;
            case 'generation_cancelled':
                setIsAiProcessing(false);
                setIsAiResponding(false);
                return;
            case 'turn_marker':
                // A turn that ended with no assistant text: the visitor
                // stopped it before anything streamed, or the turn failed.
                // Rendered as a centered notice so the transcript never
                // shows a silent gap.
                appendMessage({
                    id: data.id,
                    role: 'system',
                    type: 'turn_marker',
                    content: '',
                    created_at: data.created_at || new Date().toISOString(),
                    message_metadata: data.message_metadata,
                });
                setIsAiProcessing(false);
                return;
            case 'error':
                // Without this the "AI is working" flags stick and the Take
                // over button stays disabled until the next session switch.
                setIsAiProcessing(false);
                setIsAiResponding(false);
                if (data.error) toast.error(String(data.error), toastConfig);
                return;
            default:
                return;
        }
    }

    function handleSocketOpen({ reconnect }: { reconnect: boolean }) {
        const targetId = activeChatIdRef.current;
        if (!targetId) return;
        if (reconnect) {
            // Anything published during the outage is gone; refetch in place.
            void loadConversationRef.current(targetId, { reset: false });
        }
        // Deep links (Slack/email) may ask for an immediate takeover. Read the
        // live URL, not a closure, and attempt at most once per chat id so a
        // reconnect after a hand-back can never silently re-take the chat.
        const params = new URLSearchParams(window.location.search);
        if (params.get('auto_takeover') === '1' && autoTakeoverAttemptedForRef.current !== targetId) {
            autoTakeoverAttemptedForRef.current = targetId;
            params.delete('auto_takeover');
            const qs = params.toString();
            navigate(`${window.location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
            const attempt = (retries = 0) => {
                if (activeChatIdRef.current !== targetId) return;
                if (loadedChatIdRef.current !== targetId) {
                    // Ownership is unknown until the transcript is in.
                    if (retries < 10) setTimeout(() => attempt(retries + 1), 500);
                    return;
                }
                if (currentOwnershipState() === 'ai') handleTakeOverChat();
            };
            attempt();
        }
    }

    const socket = useConversationSocket(chatId, connectWebSocket, {
        onFrame: handleFrame,
        onOpen: handleSocketOpen,
        onClose: () => setIsAiProcessing(false),
    });

    const normalizeUrl = (url: string): string => {
        try {
            const urlObject = new URL(url);
            return `${urlObject.protocol}//${urlObject.hostname}${urlObject.pathname}`;
        } catch (error) {
            console.error('Error parsing URL:', error);
            return url;
        }
    };

    const groupMessagesByUrl = (messages: MessageType[]): GroupedMessage[] => {
        const grouped: GroupedMessage[] = [];
        let currentGroup: GroupedMessage | null = null;

        messages.forEach((message: MessageType) => {
            const fullUrl =
                message.message_metadata?.parent_window_url ||
                message.message_metadata?.url ||
                message.message_metadata?.page;

            const normalizedUrl = fullUrl
                ? normalizeUrl(fullUrl)
                : currentGroup?.url || 'Unknown URL';

            if (!currentGroup || currentGroup.url !== normalizedUrl) {
                if (currentGroup) {
                    grouped.push(currentGroup);
                }
                currentGroup = { url: normalizedUrl, messages: [] };
            }

            currentGroup.messages.push(message);
        });

        if (currentGroup) {
            grouped.push(currentGroup);
        }

        return grouped;
    };

    const sortedMessages = useMemo(() => {
        return [...messages].sort((a, b) => {
            const dateA = new Date(a.created_at.endsWith('Z') ? a.created_at : `${a.created_at}Z`);
            const dateB = new Date(b.created_at.endsWith('Z') ? b.created_at : `${b.created_at}Z`);
            return dateA.getTime() - dateB.getTime();
        });
    }, [messages]);

    const groupedMessages = useMemo(() => {
        return groupMessagesByUrl(sortedMessages);
    }, [sortedMessages]);

    const groupClickEvents = (messages: MessageType[]) => {
        const result: (MessageType | { type: 'click_group', messages: MessageType[], id: string })[] = [];
        let currentClickGroup: MessageType[] = [];

        // Question-card clicks ("Visitor clicked on QUESTION_ANSWER/QUESTION_SKIP...")
        // duplicate the answered/skipped card rendered from the system message — drop them.
        const isQuestionCardClick = (message: MessageType) =>
            message.type === 'click_event' && /clicked on QUESTION_(ANSWER|SKIP)\b/.test(message.content || '');

        messages.filter((message) => !isQuestionCardClick(message)).forEach((message, index) => {
            if (message.role === 'system' && message.type === 'click_event') {
                currentClickGroup.push(message);
            } else {
                if (currentClickGroup.length > 0) {
                    if (currentClickGroup.length > 5) {
                        result.push({
                            type: 'click_group',
                            messages: currentClickGroup,
                            id: `click_group_${index}`
                        });
                    } else {
                        result.push(...currentClickGroup);
                    }
                    currentClickGroup = [];
                }
                result.push(message);
            }
        });

        if (currentClickGroup.length > 0) {
            if (currentClickGroup.length > 5) {
                result.push({
                    type: 'click_group',
                    messages: currentClickGroup,
                    id: `click_group_final`
                });
            } else {
                result.push(...currentClickGroup);
            }
        }

        return result;
    };

    // The visitor text an AI reply answered: the nearest earlier user message
    // with content. Prefills the drawer's correction panel for replies that
    // predate context capture (captured replies use the snapshot's query).
    const precedingVisitorMessage = (messageId?: string): string | null => {
        if (!messageId) return null;
        const index = messages.findIndex((m) => m.id === messageId);
        for (let i = index - 1; i >= 0; i -= 1) {
            const candidate = messages[i];
            if (candidate.role === 'user' && typeof candidate.content === 'string' && candidate.content.trim()) {
                return candidate.content.trim();
            }
            if (candidate.role === 'assistant') break;
        }
        return null;
    };

    // Background-agent runs (services, research, proactive work) are tracked on
    // their own pages; in the transcript they only muddy the visitor/agent
    // exchange, so they are dropped here rather than rendered as a lane.
    const dropAgentRuns = (
        items: (MessageType | { type: 'click_group'; messages: MessageType[]; id: string })[]
    ): (MessageType | { type: 'click_group'; messages: MessageType[]; id: string })[] =>
        items.filter(
            (item) =>
                !(
                    'role' in item &&
                    (item as MessageType).role === 'system' &&
                    // agent_run lanes and the model-facing context_summary rows
                    // (CONTEXT_MANAGEMENT_PLAN.md) are not part of the exchange.
                    ((item as MessageType).type === 'agent_run' || (item as MessageType).type === 'context_summary')
                )
        );

    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [expandedClickTexts, setExpandedClickTexts] = useState<Set<string>>(new Set());

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    const toggleClickText = (clickId: string) => {
        setExpandedClickTexts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(clickId)) {
                newSet.delete(clickId);
            } else {
                newSet.add(clickId);
            }
            return newSet;
        });
    };

    const CLICK_TEXT_MAX_LENGTH = 80;

    const TruncatedClickText = ({ content, id }: { content: string; id: string }) => {
        const isLong = content.length > CLICK_TEXT_MAX_LENGTH;
        const isExpanded = expandedClickTexts.has(id);
        
        if (!isLong) {
            return <span className="flex-1 min-w-0 break-words">{content}</span>;
        }

        return (
            <span 
                className="flex-1 min-w-0 cursor-pointer group/clicktext"
                onClick={() => toggleClickText(id)}
                title={isExpanded ? "Click to collapse" : content}
            >
                {isExpanded ? (
                    <span className="break-words">{content}</span>
                ) : (
                    <span className="break-words">
                        {content.substring(0, CLICK_TEXT_MAX_LENGTH)}
                        <span className="text-muted-foreground">...</span>
                        <span className="text-xs text-blue-500 ml-1 group-hover/clicktext:underline">show more</span>
                    </span>
                )}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full w-full">
                <BeatLoader />
            </div>
        );
    }

    // Determine if user can interact with the chat
    // Owning the conversation is enough to compose — an inactive status or a
    // closed/offline visitor no longer blocks sending. Messages always
    // persist; on-site visitors get the chat reopened via the presence
    // channel, offline visitors see them on return.
    // Owned, not mid-takeover, and the socket for this conversation is open.
    const canInteract = ownershipState === 'owned_by_me' && !takeoverPending && socket.isOpen;
    const isVisitorOnSite = visitorPresenceState === 'on_site';
    const isVisitorOffline = visitorPresenceState === 'offline';
    const attachmentsBusy = isUploading || pendingAttachments.some(att => att.status === 'uploading' || att.status === 'failed');
    const hasOutgoingContent = Boolean(userInput.trim()) || pendingAttachments.some(att => att.status === 'uploaded');
    const composerDisabled = !socket.isOpen || takeoverPending || steerPending;
    // Steering runs a widget/app turn; email and gateway conversations have
    // their own reply paths (validate_steer refuses them), so there Enter
    // sends as it does once the rep owns the chat.
    const steerableChannel = channel !== 'email' && channel !== 'gateway';
    const steerMode = ownershipState === 'ai' && steerableChannel;
    const canSteer = steerMode && Boolean(htmlToPlainText(userInput)) && pendingAttachments.length === 0
        && !takeoverPending && !steerPending && socket.isOpen;
    const canTakeOverAndSend = ownershipState === 'ai' && hasOutgoingContent && socket.isOpen && !takeoverPending && !steerPending && !attachmentsBusy;
    const canSend = canInteract && hasOutgoingContent && !attachmentsBusy;
    const aiBusy = isAiResponding || isAiProcessing;
    const composerHint = (ownershipState === 'ai' && !steerableChannel)
        ? 'Steering is not available on this channel. Sending takes over and replies as you; the AI stays paused until you hand back.'
        : ownershipState === 'ai'
        ? (isVisitorOffline
            ? 'Visitor is offline. Steering wakes the AI now; they see its reply on return. Sending takes over, saves your message for their return and pauses the AI until you hand back.'
            : aiBusy
                ? 'The AI is replying. Steering queues behind it. Sending stops it, takes over and delivers your message.'
                : (isVisitorOnSite && status === 'inactive')
                    ? 'Visitor closed the chat. Steering tells the AI what to do next; they never see it. Sending takes over and reopens the chat on their screen.'
                    : 'Steering tells the AI what to do next; the visitor never sees it. Sending takes over and pauses the AI until you hand back.')
        : (isVisitorOffline
            ? 'The visitor has left the site. Messages are saved and shown when they return. The AI stays paused until you hand back.'
            : (isVisitorOnSite && status === 'inactive')
                ? 'The visitor closed the chat. Sending reopens it on their screen. The AI stays paused until you hand back.'
                : 'You are chatting as yourself. The AI stays paused until you hand back.');
    const visitorPresenceLabel =
        visitorPresenceState === 'on_site'
            ? 'On Site'
            : visitorPresenceState === 'offline'
                ? 'Offline'
                : 'Unknown';
    const visitorPresenceClasses =
        visitorPresenceState === 'on_site'
            ? 'bg-blue-100 text-blue-800 dark:bg-transparent dark:border dark:border-blue-500/40 dark:text-blue-300'
            : visitorPresenceState === 'offline'
                ? 'bg-gray-50 text-gray-500 border border-gray-200 dark:bg-transparent dark:border dark:border-white/15 dark:text-gray-400 dark:border-white/10'
                : 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-transparent dark:border dark:border-amber-500/40 dark:text-amber-300 dark:border-amber-800';

    // Only render the footer when there is something actionable/informational to show.
    // In particular: if the session is inactive, the visitor is offline, and AI owns the
    // session, we render nothing at the bottom.
    const showFooter =
        ownershipState === 'ai' ||
        ownershipState === 'owned_by_other' ||
        (ownershipState === 'owned_by_me' && !takeoverPending);

    return (
        <div className="flex flex-col w-full h-screen min-w-0 overflow-x-hidden">
            {loading ? (
                <div className="flex-1 flex justify-center items-center">
                    <BeatLoader />
                </div>
            ) : loadError ? (
                // A failed load used to fall through to an empty, fully
                // interactive chat still pointing at the previous session.
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                    <AlertCircle className="h-5 w-5" />
                    <span>This conversation could not be loaded.</span>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => { if (chatId) void loadConversation(chatId, { reset: true }); }}
                    >
                        Try again
                    </Button>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between p-4 md:p-6 border-b border-border dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            {onBackToSessions && (
                                <button onClick={onBackToSessions} className="md:hidden p-1 -ml-1 text-muted-foreground hover:text-foreground">
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                            )}
                            <h1 className="text-xl font-semibold dark:text-slate-100">Conversation</h1>
                        </div>
                        <div className="flex items-center gap-2">
                            {onShowDetails && (
                                <button
                                    onClick={onShowDetails}
                                    className="p-1 text-muted-foreground hover:text-foreground"
                                    title="Details"
                                    aria-label="View details"
                                >
                                    <Info className="h-5 w-5" />
                                </button>
                            )}
                            {/* Email thread chip: this transcript is an email
                                exchange — the chip carries the subject. */}
                            {channel === 'email' && (
                                <div
                                    className="inline-flex items-center max-w-[260px] px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-transparent dark:border dark:border-amber-500/40 dark:text-amber-300"
                                    title={emailSubject || undefined}
                                >
                                    <Mail className="w-3 h-3 mr-1.5 shrink-0" />
                                    <span className="truncate">{emailSubject || 'Email Thread'}</span>
                                </div>
                            )}
                            {/* Started-by badge (only when someone other than the visitor opened it) */}
                            {initiatedBy === 'ai' && (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-transparent dark:border dark:border-purple-500/40 dark:text-purple-300">
                                    <Sparkles className="w-3 h-3 mr-1.5" />
                                    AI Started
                                </div>
                            )}
                            {initiatedBy === 'human' && (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-transparent dark:border dark:border-emerald-500/40 dark:text-emerald-300">
                                    <Headset className="w-3 h-3 mr-1.5" />
                                    Rep Started
                                </div>
                            )}
                            {initiatedBy === 'sdk' && (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300">
                                    <Code className="w-3 h-3 mr-1.5" />
                                    SDK Started
                                </div>
                            )}
                            {initiatedBy === 'ai_agent' && (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300">
                                    <Bot className="w-3 h-3 mr-1.5" />
                                    AI Agent
                                </div>
                            )}
                            {/* Session Status Badge */}
                            {status && (
                                <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
                                    ${status === 'active'
                                        ? 'bg-green-100 text-green-800 dark:bg-transparent dark:border dark:border-green-500/40 dark:text-green-300'
                                        : 'bg-gray-100 text-gray-600 dark:bg-transparent dark:border dark:border-white/15 dark:text-gray-400'}`}>
                                    <MessageCircle className={`w-3 h-3 mr-1.5 ${status === 'active' ? 'animate-pulse' : ''}`} />
                                    {status === 'active' ? 'In Chat' : 'Chat Ended'}
                                </div>
                            )}
                            {/* Visitor Presence Badge */}
                            <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
                                ${visitorPresenceClasses}`}>
                                <Globe className={`w-3 h-3 mr-1.5 ${visitorPresenceState === 'on_site' ? 'animate-pulse' : ''}`} />
                                {visitorPresenceLabel}
                            </div>
                        </div>
                    </div>
                    {/* Radix wraps viewport children in a `display:table` div, which grows to fit
                        the widest message and breaks every bubble's percentage max-width. Force it
                        back to `block` so the content track stays clamped to the viewport width. */}
                    <div className="relative flex-1 min-h-0 flex flex-col">
                    <ScrollArea
                        className="flex-1 px-4 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:!w-full"
                        ref={chatRef}
                    >
                        <div className="py-4 space-y-4">
                            {/* Sentinel for infinite scroll; triggers loading older messages. */}
                            <div ref={topSentinelRef} aria-hidden className="h-px w-full" />
                            {isLoadingOlder && (
                                <div className="flex justify-center items-center py-4">
                                    <BeatLoader />
                                </div>
                            )}
                            {hasMoreMessages && !isLoadingOlder && messages.length > 0 && (
                                <div className="flex justify-center items-center py-1">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => loadOlderRef.current()}
                                        className="h-7 gap-1.5 rounded-full px-3 text-xs text-muted-foreground"
                                    >
                                        <ChevronUp className="h-3.5 w-3.5" />
                                        Load older messages
                                    </Button>
                                </div>
                            )}
                            {groupedMessages.map((group, groupIndex) => (
                                <div key={groupIndex} className="space-y-4 min-w-0">
                                    {/* Page transition divider — shows which page the visitor was on. */}
                                    <div className="flex items-center gap-3 px-2 py-1">
                                        <div className="h-px flex-1 bg-border" />
                                        <div className="flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 max-w-[80%] min-w-0">
                                            <Globe className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                            <span className="truncate text-xs font-medium text-muted-foreground" title={group.url}>
                                                {group.url === 'Unknown URL' ? group.url : group.url.replace(/^https?:\/\//, '')}
                                            </span>
                                        </div>
                                        <div className="h-px flex-1 bg-border" />
                                    </div>
                                    <div className="space-y-4">
                                        {dropAgentRuns(groupClickEvents(group.messages)).map((item, index) => {
                                            const isClickGroup = 'type' in item && item.type === 'click_group';
                                            const clickGroup = item as { type: 'click_group', messages: MessageType[], id: string };
                                            const messageItem = item as MessageType;

                                            return (
                                                <div key={index}>
                                                    {isClickGroup ? (
                                                        <div className="flex w-full justify-center">
                                                            <div className="w-full max-w-[90%] min-w-0 rounded-lg border border-border bg-card px-3 py-1.5 shadow-sm">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleGroup(clickGroup.id)}
                                                                    className="flex w-full items-center gap-2 text-left"
                                                                >
                                                                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted">
                                                                        <MousePointerClick className="h-3 w-3 text-muted-foreground" />
                                                                    </div>
                                                                    <span className="text-[13px] font-medium text-foreground whitespace-nowrap">
                                                                        {clickGroup.messages.length} click events
                                                                    </span>
                                                                    <span className="ml-auto text-[10px] text-muted-foreground/60">
                                                                        {formatDate(clickGroup.messages[0]?.created_at || '', 'compact')} – {formatDate(clickGroup.messages[clickGroup.messages.length - 1]?.created_at || '', 'compact')}
                                                                    </span>
                                                                    {expandedGroups.has(clickGroup.id) ? (
                                                                        <ChevronUp className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                                                    ) : (
                                                                        <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                                                    )}
                                                                </button>
                                                                {expandedGroups.has(clickGroup.id) && (
                                                                    <div className="mt-1.5 space-y-1.5 border-t border-border pt-1.5">
                                                                        {clickGroup.messages.map((clickMessage: MessageType, clickIndex: number) => (
                                                                            <div key={clickIndex} className="flex items-center gap-2 pl-7 text-xs text-muted-foreground">
                                                                                <TruncatedClickText
                                                                                    content={clickMessage.content}
                                                                                    id={`${clickGroup.id}_${clickIndex}`}
                                                                                />
                                                                                <span className="ml-auto flex-shrink-0 text-[10px] text-muted-foreground/60">
                                                                                    {formatDate(clickMessage.created_at || '', 'compact')}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {(messageItem.role === 'user' || messageItem.role === 'assistant') && (
                                                                <Message
                                                                    role={messageItem.role}
                                                                    content={messageItem.content}
                                                                    created_at={messageItem.created_at}
                                                                    human_agent={messageItem.human_agent}
                                                                    message_metadata={messageItem.message_metadata}
                                                                    conversationId={conversationId}
                                                                    messageId={user ? messageItem.id : undefined}
                                                                    agentName={messageItem.agent_name || conversation?.agent?.name}
                                                                    visitorIsAiAgent={initiatedBy === 'ai_agent'}
                                                                    question={messageItem.question}
                                                                    moderationScan={user ? messageItem.moderation_scan : undefined}
                                                                    visitorQuestion={messageItem.role === 'assistant' ? precedingVisitorMessage(messageItem.id) : null}
                                                                    escalations={user ? escalations : undefined}
                                                                    onQuestionChange={(question) => {
                                                                        if (!messageItem.id) return;
                                                                        setMessages((prev) =>
                                                                            prev.map((m) => (m.id === messageItem.id ? { ...m, question } : m))
                                                                        );
                                                                    }}
                                                                />
                                                            )}
                                                            {messageItem.role === 'system' && isQuestionCardActivity(messageItem) && (
                                                                <QuestionCardActivity message={messageItem} />
                                                            )}
                                                            {messageItem.role === 'system' && messageItem.type !== 'agent_run' && !isQuestionCardActivity(messageItem) && (
                                                                <div className="flex flex-col items-center space-y-1">
                                                                    {messageItem.type === 'take_over_chat' && (
                                                                        <div className="flex w-full justify-center">
                                                                            <div className="flex items-center gap-2 bg-muted/50 border border-border px-3 py-1.5 rounded-lg max-w-[90%] min-w-0 text-sm">
                                                                                {messageItem.human_agent?.profile_photo_url ? (
                                                                                    <Avatar className="h-5 w-5 border">
                                                                                        <AvatarImage src={messageItem.human_agent.profile_photo_url} alt={getHumanAgentName(messageItem.human_agent)} />
                                                                                        <AvatarFallback className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px]">
                                                                                            {getHumanAgentInitials(messageItem.human_agent, messageItem.agent_name)}
                                                                                        </AvatarFallback>
                                                                                    </Avatar>
                                                                                ) : (
                                                                                    <Headset className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                                                                                )}
                                                                                <span className="truncate">
                                                                                    <span className="font-medium text-emerald-600 dark:text-emerald-400">{messageItem.agent_name || getHumanAgentName(messageItem.human_agent)}</span>
                                                                                    <span className="text-muted-foreground"> took over</span>
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {messageItem.type === 'hand_back_chat' && (
                                                                        <div className="flex w-full justify-center">
                                                                            <div className="flex items-center gap-2 bg-muted/50 border border-border px-3 py-1.5 rounded-lg max-w-[90%] min-w-0 text-sm">
                                                                                {messageItem.human_agent?.profile_photo_url ? (
                                                                                    <Avatar className="h-5 w-5 border">
                                                                                        <AvatarImage src={messageItem.human_agent.profile_photo_url} alt={getHumanAgentName(messageItem.human_agent)} />
                                                                                        <AvatarFallback className="bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px]">
                                                                                            {getHumanAgentInitials(messageItem.human_agent, messageItem.agent_name)}
                                                                                        </AvatarFallback>
                                                                                    </Avatar>
                                                                                ) : (
                                                                                    <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                                                                                )}
                                                                                <span className="truncate">
                                                                                    <span className="font-medium text-amber-600 dark:text-amber-400">{messageItem.agent_name || getHumanAgentName(messageItem.human_agent)}</span>
                                                                                    <span className="text-muted-foreground"> handed back to the AI</span>
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {messageItem.type === 'click_event' && (
                                                                        <div className="flex w-full justify-center">
                                                                            <div className="flex items-center gap-2 bg-muted/50 border border-border px-3 py-1.5 rounded-lg max-w-[90%] min-w-0 text-sm text-muted-foreground">
                                                                                <MousePointerClick className="h-3.5 w-3.5 flex-shrink-0" />
                                                                                <TruncatedClickText
                                                                                    content={messageItem.content}
                                                                                    id={`click_${groupIndex}_${index}`}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {messageItem.message_metadata?.system_type === 'operator_steer' && (
                                                                        <div className="flex w-full justify-center">
                                                                            <div className="flex flex-col gap-1 bg-amber-50/60 dark:bg-transparent border border-amber-200/70 dark:border-amber-500/40 px-3 py-2 rounded-lg max-w-[90%] min-w-0 text-sm">
                                                                                <div className="flex items-center gap-2 min-w-0">
                                                                                    {messageItem.human_agent?.profile_photo_url ? (
                                                                                        <Avatar className="h-5 w-5 border">
                                                                                            <AvatarImage src={messageItem.human_agent.profile_photo_url} alt={getHumanAgentName(messageItem.human_agent)} />
                                                                                            <AvatarFallback className="bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px]">
                                                                                                {getHumanAgentInitials(messageItem.human_agent, messageItem.message_metadata?.steered_by_name)}
                                                                                            </AvatarFallback>
                                                                                        </Avatar>
                                                                                    ) : (
                                                                                        <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                                                                                    )}
                                                                                    <span className="truncate">
                                                                                        <span className="font-medium text-amber-700 dark:text-amber-300">
                                                                                            {messageItem.message_metadata?.steered_by_name || getHumanAgentName(messageItem.human_agent) || 'A teammate'}
                                                                                        </span>
                                                                                        <span className="text-muted-foreground">
                                                                                            {messageItem.message_metadata?.steer_mode === 'next_turn' ? ' steered the AI for its next reply' : ' steered the AI'}
                                                                                        </span>
                                                                                    </span>
                                                                                </div>
                                                                                <p className="whitespace-pre-wrap break-words text-foreground/90">{messageItem.content}</p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {messageItem.type !== 'take_over_chat' && messageItem.type !== 'hand_back_chat' && messageItem.type !== 'click_event' && messageItem.message_metadata?.system_type !== 'operator_steer' && (() => {
                                                                        const meta = messageItem.message_metadata || {};
                                                                        const systemType = meta.system_type;
                                                                        const config: { icon: React.ReactNode; label: string; color: string } = (() => {
                                                                            switch (systemType) {
                                                                                case 'form_submission':
                                                                                    return { icon: <FileText className="h-3.5 w-3.5 flex-shrink-0" />, label: 'Form submitted', color: 'text-blue-600 dark:text-blue-400' };
                                                                                case 'escalation_button':
                                                                                    return { icon: <PhoneCall className="h-3.5 w-3.5 flex-shrink-0" />, label: 'Requested a human', color: 'text-purple-600 dark:text-purple-400' };
                                                                                case 'assessment_start':
                                                                                    return { icon: <ClipboardList className="h-3.5 w-3.5 flex-shrink-0" />, label: 'Assessment started', color: 'text-indigo-600 dark:text-indigo-400' };
                                                                                case 'calendly_booking':
                                                                                    return { icon: <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />, label: 'Meeting scheduled', color: 'text-emerald-600 dark:text-emerald-400' };
                                                                                case 'lead_landing':
                                                                                    return { icon: <UserPlus className="h-3.5 w-3.5 flex-shrink-0" />, label: 'Known lead arrived', color: 'text-teal-600 dark:text-teal-400' };
                                                                                case 'action_result':
                                                                                    return { icon: <Cog className="h-3.5 w-3.5 flex-shrink-0" />, label: 'Action completed', color: 'text-gray-600 dark:text-gray-400' };
                                                                                case 'generation_stopped':
                                                                                    return { icon: <CircleStop className="h-3.5 w-3.5 flex-shrink-0" />, label: 'Visitor stopped the reply', color: 'text-amber-600 dark:text-amber-400' };
                                                                                case 'generation_failed':
                                                                                    return { icon: <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />, label: 'AI failed to respond', color: 'text-red-600 dark:text-red-400' };
                                                                                default:
                                                                                    return { icon: <Info className="h-3.5 w-3.5 flex-shrink-0" />, label: 'System', color: 'text-muted-foreground' };
                                                                            }
                                                                        })();
                                                                        // Pull the meaningful detail per type rather than the raw
                                                                        // (often verbose) system message content.
                                                                        const detail: string = (() => {
                                                                            if (systemType === 'escalation_button') {
                                                                                return meta.escalation_button_caption || '';
                                                                            }
                                                                            if (systemType === 'form_submission') {
                                                                                const emailMatch = (messageItem.content || '').match(/\(([^)]+)\)/);
                                                                                return emailMatch ? emailMatch[1] : '';
                                                                            }
                                                                            if (systemType === 'generation_stopped') {
                                                                                if (meta.stopped_during === 'tool_calls') {
                                                                                    return 'while the agent was working';
                                                                                }
                                                                                return 'before any text was sent';
                                                                            }
                                                                            if (systemType === 'generation_failed') {
                                                                                return meta.error_type || '';
                                                                            }
                                                                            return messageItem.content || '';
                                                                        })();
                                                                        return (
                                                                            <div className="flex w-full justify-center">
                                                                                <div className="flex items-center gap-2 bg-muted/50 border border-border px-3 py-1.5 rounded-lg max-w-[90%] min-w-0 text-sm">
                                                                                    <span className={cn("flex items-center", config.color)}>{config.icon}</span>
                                                                                    <span className={cn("font-medium flex-shrink-0", config.color)}>{config.label}</span>
                                                                                    {detail && (
                                                                                        <span className="min-w-0 truncate text-muted-foreground" title={detail}>{detail}</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                    <span className="text-[11px] text-muted-foreground/70">
                                                                        {formatDate(messageItem.created_at)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            {(isVisitorTyping || isAiResponding) && (
                                <Message
                                    role={isVisitorTyping ? "user" : "assistant"}
                                    content=""
                                    isVisitorTyping={true}
                                    conversationId={conversationId}
                                />
                            )}
                        </div>
                    </ScrollArea>
                    {scrolledUp && (
                        <button
                            type="button"
                            onClick={jumpToLatest}
                            aria-label={hasUnseenMessages ? 'New messages, jump to latest' : 'Jump to latest'}
                            className={cn(
                                "absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 rounded-full border shadow-md text-xs font-medium transition-colors",
                                hasUnseenMessages
                                    ? "bg-primary text-primary-foreground border-primary px-3 py-1.5 hover:bg-primary/90"
                                    : "bg-background text-muted-foreground p-1.5 hover:text-foreground"
                            )}
                        >
                            {hasUnseenMessages && <span>New messages</span>}
                            <ChevronDown className="w-4 h-4" />
                        </button>
                    )}
                    </div>

                    {showFooter && (
                        <div className="border-t p-4 space-y-3">
                            {/* Owned by another agent: view-only */}
                            {ownershipState === 'owned_by_other' && (
                                <div className="flex items-center gap-3 p-3 bg-muted border rounded-lg">
                                    <Avatar className="h-8 w-8 border flex-shrink-0">
                                        <AvatarImage src={ownerAgentPhotoUrl || undefined} alt={ownerAgentName || 'Rep'} />
                                        <AvatarFallback className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
                                            {ownerAgentName?.[0]?.toUpperCase() || 'A'}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm">
                                        Visitor currently chatting with <span className="font-semibold">{ownerAgentName}</span>
                                    </span>
                                </div>
                            )}

                            {/* One composer for both audiences. While the AI owns the
                                conversation the text can steer the AI or become the rep's
                                first message (the claim and the message go together, so
                                the visitor never waits on a silent rep). Once owned, it is
                                a plain Send. */}
                            {(ownershipState === 'ai' || ownershipState === 'owned_by_me') && (
                                <div className="space-y-2">
                                    {pendingAttachments.length > 0 && (
                                        <div className="flex flex-wrap gap-2 p-2 bg-muted/30 rounded-lg">
                                            {pendingAttachments.map((attachment) => {
                                                const isImage = attachment.type.startsWith('image/');
                                                const isPdf = attachment.type === 'application/pdf';
                                                const isDoc = attachment.type?.includes('word') || attachment.type?.includes('document');
                                                const isUploading = attachment.status === 'uploading';
                                                const isFailed = attachment.status === 'failed';

                                                const formatFileSize = (bytes: number) => {
                                                    if (bytes < 1024) return bytes + ' B';
                                                    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                                                    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                                                };

                                                return (
                                                    <div
                                                        key={attachment.id}
                                                        className={cn(
                                                            "flex items-center space-x-2 bg-background border rounded-lg px-3 py-2 pr-2 group transition-colors",
                                                            isUploading && "animate-pulse border-blue-300",
                                                            isFailed && "border-destructive bg-destructive/5",
                                                            !isUploading && !isFailed && "hover:border-primary/50"
                                                        )}
                                                    >
                                                        {isUploading ? (
                                                            <Loader2 className="h-4 w-4 text-blue-500 flex-shrink-0 animate-spin" />
                                                        ) : isFailed ? (
                                                            <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                                                        ) : (
                                                            <>
                                                                {isImage && <ImageIconLucide className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                                                                {isPdf && <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />}
                                                                {isDoc && <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                                                                {!isImage && !isPdf && !isDoc && <FileIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                                                            </>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className={cn(
                                                                "text-sm font-medium truncate",
                                                                isFailed && "text-destructive"
                                                            )}>
                                                                {attachment.filename}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {isUploading ? 'Uploading...' : isFailed ? 'Upload failed' : formatFileSize(attachment.size)}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className={cn(
                                                                "h-6 w-6 p-0 transition-opacity",
                                                                isFailed ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                                                            )}
                                                            onClick={() => removeAttachment(attachment.id)}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <RichTextEditor
                                        key={ownershipState === 'ai' ? 'composer-ai' : 'composer-rep'}
                                        content={userInput}
                                        onChange={handleInputChange}
                                        onSubmit={steerMode ? () => { void handleSteer(); } : (ownershipState === 'ai' ? handleTakeOverAndSend : sendMessage)}
                                        onModSubmit={steerMode ? handleTakeOverAndSend : undefined}
                                        placeholder={steerMode
                                            ? 'Tell the AI what to do next… (Shift+Enter for new line)'
                                            : 'Message the visitor… (Shift+Enter for new line)'}
                                        className="flex-1"
                                        disabled={composerDisabled}
                                        showToolbar={true}
                                        enableFileAttachments={true}
                                        onFileUpload={handleFileUpload}
                                        onUploadStart={() => setIsUploading(true)}
                                        onUploadEnd={() => setIsUploading(false)}
                                        toolbarEnd={steerMode ? (
                                            // What Enter does. Flips the moment ownership changes.
                                            <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 py-0.5 pl-0.5 pr-2 text-[11px] font-medium text-amber-900 dark:border-amber-500/40 dark:bg-transparent dark:text-amber-200">
                                                <kbd className="inline-flex items-center rounded border border-amber-400 border-b-2 bg-white px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-amber-900 dark:border-amber-500/50 dark:bg-transparent dark:text-amber-100">Enter</kbd>
                                                steers the AI
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 py-0.5 pl-0.5 pr-2 text-[11px] font-medium text-emerald-900 dark:border-emerald-500/40 dark:bg-transparent dark:text-emerald-200">
                                                <kbd className="inline-flex items-center rounded border border-emerald-400 border-b-2 bg-white px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-emerald-900 dark:border-emerald-500/50 dark:bg-transparent dark:text-emerald-100">Enter</kbd>
                                                sends to visitor
                                            </span>
                                        )}
                                    />

                                    <div className="flex items-start justify-between gap-3">
                                        <p className="text-[11px] leading-snug text-muted-foreground min-w-0 pt-1.5">
                                            {composerHint}
                                        </p>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            {ownershipState === 'ai' && (
                                                <>
                                                    <Button
                                                        variant={steerMode ? 'outline' : 'default'}
                                                        size="sm"
                                                        className="gap-1"
                                                        onClick={handleTakeOverAndSend}
                                                        disabled={!canTakeOverAndSend}
                                                        title="Take over and send this to the visitor. The AI pauses until you hand back."
                                                    >
                                                        {takeoverPending ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        ) : (
                                                            <Headset className="h-3.5 w-3.5" />
                                                        )}
                                                        {takeoverPending ? 'Taking over…' : aiBusy ? 'Stop AI & send' : 'Send'}
                                                        {!takeoverPending && <Kbd>{steerMode ? MOD_ENTER_KEY : ENTER_KEY}</Kbd>}
                                                    </Button>
                                                    {steerMode && (
                                                    <Button
                                                        size="sm"
                                                        className="gap-1 bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-600 dark:text-white dark:hover:bg-amber-500"
                                                        onClick={() => { void handleSteer(); }}
                                                        disabled={!canSteer}
                                                        title="Guide the AI without taking over. The visitor never sees this."
                                                    >
                                                        {steerPending ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        ) : (
                                                            <Sparkles className="h-3.5 w-3.5" />
                                                        )}
                                                        Steer AI
                                                        {!steerPending && <Kbd>{ENTER_KEY}</Kbd>}
                                                    </Button>
                                                    )}
                                                </>
                                            )}
                                            {ownershipState === 'owned_by_me' && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={handleHandBackChat}
                                                        disabled={!socket.isOpen || takeoverPending}
                                                    >
                                                        Hand back to AI
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        className="gap-1"
                                                        onClick={sendMessage}
                                                        disabled={!canSend}
                                                    >
                                                        <SendHorizontal className="h-3.5 w-3.5" />
                                                        Send
                                                        <Kbd>{ENTER_KEY}</Kbd>
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};


export default Chat;
